/**
 * Checkpoint System Types
 * For saving and restoring code state
 */

export interface GitState {
  branch: string;
  commit: string;
  stagedFiles: string[];
  modifiedFiles: string[];
  untrackedFiles: string[];
  stashId?: string;
}

export interface FileSnapshot {
  path: string;
  content: string;
  exists: boolean;
  permissions?: string;
}

export interface Checkpoint {
  /** Unique checkpoint ID */
  id: string;

  /** When the checkpoint was created */
  timestamp: string;

  /** Git state at checkpoint time */
  gitState: GitState;

  /** Conversation index at checkpoint */
  conversationIndex: number;

  /** Description of what triggered the checkpoint */
  description: string;

  /** Tool that triggered the checkpoint */
  triggerTool?: string;

  /** Files that were about to be modified */
  targetFiles?: string[];

  /** Snapshots of files before modification */
  fileSnapshots?: FileSnapshot[];

  /** Session ID */
  sessionId: string;

  /** Whether this checkpoint has been rewound to */
  rewoundAt?: string;

  /** Metadata */
  metadata?: Record<string, any>;
}

export interface CheckpointConfig {
  /** Whether checkpoints are enabled */
  enabled: boolean;

  /** Maximum number of checkpoints to keep */
  maxCheckpoints: number;

  /** Tools that trigger checkpoints */
  triggerTools: string[];

  /** Whether to include file snapshots (more storage but safer) */
  includeFileSnapshots: boolean;

  /** Auto-cleanup checkpoints older than N hours */
  autoCleanupHours?: number;
}

export interface RewindOptions {
  /** Restore git state */
  restoreGit: boolean;

  /** Restore conversation */
  restoreConversation: boolean;

  /** Restore file snapshots (if available) */
  restoreFiles: boolean;

  /** Create a backup checkpoint before rewinding */
  createBackup: boolean;
}

export interface RewindResult {
  success: boolean;
  checkpoint: Checkpoint;
  restoredGit: boolean;
  restoredConversation: boolean;
  restoredFiles: string[];
  backupCheckpointId?: string;
  error?: string;
}

export const DEFAULT_CHECKPOINT_CONFIG: CheckpointConfig = {
  enabled: true,
  maxCheckpoints: 50,
  triggerTools: [
    'write_file',
    'edit_file',
    'append_file',
    'move_file',
    'delete_file',
    'create_directory',
    'git_commit',
    'git_checkout',
    'git_merge',
    'git_rebase',
    'bash', // Only for commands that modify files
  ],
  includeFileSnapshots: true,
  autoCleanupHours: 24,
};

export const DEFAULT_REWIND_OPTIONS: RewindOptions = {
  restoreGit: true,
  restoreConversation: true,
  restoreFiles: true,
  createBackup: true,
};
